import { type FormatCategoryWithOther as FormatCategory } from "@garimpa/lib/formatGroups";

interface FormatIconProps {
  format: FormatCategory | "all";
  className?: string;
  size?: number;
}

/**
 * SVG icons for physical media format categories.
 * Uses currentColor for theme compatibility.
 */
export function FormatIcon({ format, className = "", size = 20 }: FormatIconProps) {
  const props = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.5,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    className,
  };

  switch (format) {
    case "vinyl":
      return <VinylIcon {...props} />;
    case "cd":
      return <CDIcon {...props} />;
    case "cassette":
      return <CassetteIcon {...props} />;
    case "video":
      return <VideoIcon {...props} />;
    case "all":
      return <AllFormatsIcon {...props} />;
    default:
      return <OtherIcon {...props} />;
  }
}

function VinylIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...props}>
      {/* Outer ring */}
      <circle cx="12" cy="12" r="10" />
      {/* Grooves */}
      <circle cx="12" cy="12" r="7" strokeOpacity="0.5" />
      <circle cx="12" cy="12" r="4" strokeOpacity="0.3" />
      {/* Label */}
      <circle cx="12" cy="12" r="3" fill="currentColor" fillOpacity="0.3" stroke="none" />
      {/* Center hole */}
      <circle cx="12" cy="12" r="1" fill="currentColor" />
    </svg>
  );
}

function CDIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...props}>
      {/* Outer ring */}
      <circle cx="12" cy="12" r="10" />
      {/* Inner ring */}
      <circle cx="12" cy="12" r="4" />
      {/* Center hole */}
      <circle cx="12" cy="12" r="1.5" fill="currentColor" />
      {/* Reflection */}
      <path d="M8 8 L6 6" strokeOpacity="0.5" />
      <path d="M16 8 L18 6" strokeOpacity="0.5" />
    </svg>
  );
}

function CassetteIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...props}>
      {/* Cassette body */}
      <rect x="2" y="6" width="20" height="12" rx="2" />
      {/* Label area */}
      <rect x="4" y="8" width="16" height="5" rx="1" strokeOpacity="0.5" />
      {/* Tape reels */}
      <circle cx="7" cy="14" r="2" />
      <circle cx="17" cy="14" r="2" />
      {/* Tape window */}
      <path d="M9 14 L15 14" strokeOpacity="0.5" />
    </svg>
  );
}

function AllFormatsIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...props}>
      {/* Stack of discs */}
      <ellipse cx="12" cy="16" rx="8" ry="3" />
      <ellipse cx="12" cy="13" rx="8" ry="3" />
      <ellipse cx="12" cy="10" rx="8" ry="3" />
      {/* Top disc center */}
      <ellipse cx="12" cy="10" rx="2" ry="0.8" fill="currentColor" fillOpacity="0.3" />
    </svg>
  );
}

function OtherIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...props}>
      {/* Generic disc */}
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="3" />
      {/* Question mark */}
      <text
        x="12"
        y="13"
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize="6"
        fill="currentColor"
        stroke="none"
      >
        ?
      </text>
    </svg>
  );
}

function VideoIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...props}>
      {/* Disc outline */}
      <circle cx="12" cy="12" r="10" />
      {/* Inner ring */}
      <circle cx="12" cy="12" r="4" strokeOpacity="0.4" />
      {/* Center hole */}
      <circle cx="12" cy="12" r="1.5" fill="currentColor" />
      {/* Play triangle */}
      <path
        d="M10.5 8.5 L15.5 12 L10.5 15.5 Z"
        fill="currentColor"
        fillOpacity="0.4"
        stroke="none"
      />
    </svg>
  );
}
